import React from 'react';
import {
  DocPage, PageHeader, Section,
  Para, Callout, Prerequisites, NextStep,
} from '../components/DocBlocks';
import { useIndustry } from '../IndustryContext';
import './BillingReport.css';

const TOC = [
  { id: 'overview',    label: 'Overview' },
  { id: 'generation',  label: 'When it is generated' },
  { id: 'convergence', label: 'Multiple fees, one report' },
  { id: 'contents',    label: 'What it contains' },
  { id: 'approval',    label: 'Approve or reject' },
];

// ── Billing report card ───────────────────────────────────────────────────────

function BillingReportCard({ report }) {
  const { reportId, customer, period, rows, total } = report;
  return (
    <div className="br-card">
      <div className="br-card-header">
        <div className="br-card-header-left">
          <span className="br-card-id">{reportId}</span>
          <span className="br-card-meta">
            <strong>{customer}</strong>
            {' · '}Billing period: {period.start} – {period.end}
          </span>
        </div>
        <span className="br-status br-status--pending">Pending Review</span>
      </div>
      <table className="br-card-table">
        <thead>
          <tr>
            <th>Fee</th>
            <th>Qty</th>
            <th>Rate</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.name}>
              <td>
                <span className="br-fee-name">{row.name}</span>
                {row.note && <span className="br-fee-note">{row.note}</span>}
              </td>
              <td>{row.qty}</td>
              <td>{row.rate}</td>
              <td>{row.amount}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="br-card-footer">
        <div className="br-card-total">
          <span className="br-card-total-label">Total</span>
          {total}
        </div>
        <div className="br-card-actions">
          <button className="br-btn br-btn--reject" disabled>Reject</button>
          <button className="br-btn br-btn--approve" disabled>Approve</button>
        </div>
      </div>
    </div>
  );
}

// ── Convergence grid ──────────────────────────────────────────────────────────

function ConvergenceGrid({ convergence }) {
  const { fees, dates } = convergence;
  const colCount = 1 + dates.length;
  const gridCols = `repeat(${colCount}, 1fr)`;

  return (
    <div className="br-convergence">
      <div className="br-convergence-header" style={{ gridTemplateColumns: gridCols }}>
        <div className="br-convergence-col-header">Fee</div>
        {dates.map((d) => (
          <div key={d.label} className="br-convergence-col-header">{d.label}</div>
        ))}
      </div>

      {fees.map((fee) => (
        <div key={fee.name} className="br-convergence-row" style={{ gridTemplateColumns: gridCols }}>
          <div className="br-convergence-cell">
            {fee.name}
            <span className="br-fee-note" style={{ marginLeft: 6, display: 'inline' }}>({fee.cycle})</span>
          </div>
          {dates.map((d) => {
            const hits = d.fees.includes(fee.name);
            return (
              <div key={d.label} className="br-convergence-cell">
                <span className={`br-convergence-dot br-convergence-dot--${hits ? 'hit' : 'miss'}`}>
                  {hits ? '✓' : '—'}
                </span>
              </div>
            );
          })}
        </div>
      ))}

      <div className="br-convergence-summary" style={{ gridTemplateColumns: gridCols }}>
        <div className="br-convergence-summary-cell">Billing Report</div>
        {dates.map((d) => (
          <div key={d.label} className="br-convergence-summary-cell">
            {d.fees.length > 0
              ? <span className="br-convergence-report-badge">{d.fees.length} fee{d.fees.length > 1 ? 's' : ''}</span>
              : <span className="br-convergence-no-report">—</span>
            }
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Approval flow ─────────────────────────────────────────────────────────────

function ApprovalFlow() {
  return (
    <div className="br-approval-flow">
      <div className="br-approval-node br-approval-node--pending">
        <div className="br-approval-node-icon">◎</div>
        <div className="br-approval-node-label">Pending Review</div>
        <div className="br-approval-node-desc">Billing cycle ends. Report generated and waiting for action.</div>
      </div>
      <div className="br-approval-fork">
        <div className="br-approval-branch">
          <div className="br-approval-branch-arrow">
            <span className="br-approval-branch-label">Approve</span>
            →
          </div>
          <div className="br-approval-node br-approval-node--approved">
            <div className="br-approval-node-icon">✓</div>
            <div className="br-approval-node-label">Approved</div>
            <div className="br-approval-node-desc">Report converts to an invoice. Customer can be billed.</div>
          </div>
        </div>
        <div className="br-approval-branch">
          <div className="br-approval-branch-arrow">
            <span className="br-approval-branch-label">Reject</span>
            →
          </div>
          <div className="br-approval-node br-approval-node--rejected">
            <div className="br-approval-node-icon">✕</div>
            <div className="br-approval-node-label">Rejected</div>
            <div className="br-approval-node-desc">Report is not invoiced. No charge is sent to the customer.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function BillingReport({ onNavigate }) {
  const { industry } = useIndustry();
  const br = industry.billingReport;

  return (
    <DocPage toc={TOC}>
      <PageHeader
        eyebrow="Operations"
        title="Billing Report"
        description="A billing report is the calculated summary of charges at the end of a fee's billing cycle — the complete picture of what was consumed and what it costs, before it becomes an invoice."
      />

      <Prerequisites
        onNavigate={onNavigate}
        items={[
          { id: 'usage',    label: 'Usage' },
          { id: 'contract', label: 'Contracts' },
        ]}
      />

      <Section id="overview" title="Overview">
        <Para>
          When a fee's billing cycle closes, the billing engine has everything it needs:
          the accumulated usage for the period, the rate card, and the pricing context.
          It runs the calculation and produces a billing report — a document that captures
          the result before anything is sent to the customer.
        </Para>
        <Para>
          The billing report is the last checkpoint before invoicing. It shows what was
          consumed, how the rate was applied, and what the total comes to. A user reviews
          it and either approves it — converting it to an invoice — or rejects it.
        </Para>
        <Callout type="info" title="Billing reports belong to fees, not contracts">
          The contract itself has no billing period. Each fee inside a contract has its own
          billing cycle, and billing reports are generated at the fee level. A single contract
          can generate billing reports on different dates for different fees.
        </Callout>
      </Section>

      <Section id="generation" title="When it is generated">
        <Para>
          Every fee in a contract has a billing cycle — monthly, quarterly, annually, or a
          custom duration. When that cycle ends, the billing engine closes the accumulation
          window, applies the rate card to the total quantity, and produces a billing report
          for that fee.
        </Para>
        <Para>
          The billing report is created automatically. There is no manual trigger required.
          Once the cycle end date passes, the system generates the report and places it in
          a pending review state.
        </Para>
        <Callout type="tip" title="Flat-rate fees also generate billing reports">
          Usage-based fees generate billing reports when the accumulated quantity is priced.
          Flat-rate fees also go through the same process — the charge is fixed, but the
          approval step still applies before an invoice is issued.
        </Callout>
      </Section>

      <Section id="convergence" title="Multiple fees, one report">
        <Para>
          Each fee has an independent billing cycle. Two fees in the same contract can have
          different cycles — one monthly, one quarterly. They generate billing reports on
          different schedules and produce separate invoices.
        </Para>
        <Para>
          However, when multiple fees happen to end their cycle on the same date, they are
          grouped into a single billing report. This is convergence — different fee cycles
          landing on the same billing date.
        </Para>
        <ConvergenceGrid convergence={br.convergence} />
        <p className="br-cycle-note">
          {br.convergenceNote}
        </p>
        <Para>
          In the example above, {br.convergence.fees.filter(f => f.cycle === 'monthly').map(f => f.name).join(' and ')} share
          a monthly cycle — they converge on every billing date. {br.convergence.fees.find(f => f.cycle !== 'monthly')?.name} runs
          on a longer cycle and only joins the report when its own cycle end aligns.
        </Para>
        <Callout type="info" title="One billing report per billing date per customer">
          All fees that share a billing date end up in the same billing report. You approve
          or reject them together — there is no way to approve one fee and reject another
          within the same report.
        </Callout>
      </Section>

      <Section id="contents" title="What it contains">
        <Para>
          Each billing report contains a line item for every fee that contributed to it.
          For each line, the report shows the accumulated quantity, the rate that was applied,
          and the resulting charge. Here is an example:
        </Para>
        <BillingReportCard report={br.report} />
        <Para>
          The line items map directly to fee components on the contract. The quantities
          are the totals accumulated over the billing period. The rates come from the
          rate card in effect at period end — including any time-based overrides or
          discounts configured on the fee.
        </Para>
        <Callout type="tip" title="One line per fee per pricing dimension combination">
          If a fee uses pricing dimensions, each unique dimension combination produces its
          own line in the report — the same way it would appear on an invoice. A fee priced
          across two zones produces two lines.
        </Callout>
      </Section>

      <Section id="approval" title="Approve or reject">
        <Para>
          Every billing report starts in a pending review state. It must be explicitly
          approved before it becomes an invoice.
        </Para>
        <ApprovalFlow />
        <Para>
          <strong>Approving</strong> the report converts it to an invoice. The invoice
          carries the same line items and totals as the report. From there, the normal
          invoicing and payment collection process begins.
        </Para>
        <Para>
          <strong>Rejecting</strong> the report prevents it from becoming an invoice.
          The charges are not sent to the customer. Rejection is typically used when
          something looks wrong — a rate card misconfiguration, an unexpected usage
          spike, or a contractual dispute — and the billing team needs to investigate
          before proceeding.
        </Para>
        <Callout type="warning" title="Rejection does not reverse accumulated usage">
          Rejecting a billing report does not delete the usage records that contributed
          to it. The usage is still in the system. Rejection only prevents the current
          report from being invoiced — it is a review gate, not a data correction tool.
        </Callout>
      </Section>

      <NextStep
        onNavigate={onNavigate}
        items={[
          { id: 'usage',    title: 'Usage',     desc: 'How usage events are ingested and accumulated over the billing period.' },
          { id: 'contract', title: 'Contracts',  desc: 'Where billing cycles are configured per fee.' },
        ]}
      />
    </DocPage>
  );
}
